import {
  MagicLinkRequestSchema,
  PortalMeSchema,
  type MagicLinkRequest,
  type PortalMe,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, isPortalAuthError, portalFetch } from "./portal-client.js";

export { ApiError };

/**
 * Portal-side hooks.
 *
 * Three surfaces:
 *   - `usePortalMe()` — gates the portal layout. 401 means "not
 *     signed in"; 200 returns the session-derived user info.
 *   - `useRequestMagicLink()` — the sign-in form action.
 *   - `useSignOut()` — clears the session cookie + invalidates
 *     `me` so the layout redirects to /portal/sign-in.
 *
 * Validation: every response is parsed against the SSOT Zod
 * schema in `@l30/shared`. Drift surfaces as ApiContractError
 * rather than rendering with a stale shape.
 */

const PORTAL_ME_KEY = ["portal", "me"] as const;

export function usePortalMe() {
  return useQuery<PortalMe>({
    queryKey: PORTAL_ME_KEY,
    queryFn: () => portalFetch("/portal/me", {}, PortalMeSchema),
    // Session validity is server-side; a real 401/403 means "not
    // signed in" and the global handler routes to sign-in. A
    // transient (network / 5xx) blip should NOT bounce a valid
    // session — retry it once and let the shell show a Retry
    // instead (assessment U1). Don't poll: no value, just noise.
    retry: (failureCount, error) => !isPortalAuthError(error) && failureCount < 1,
    refetchOnWindowFocus: false,
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: async (input: MagicLinkRequest | string) => {
      // Accept `{ email, turnstileToken? }` (preferred) or a bare
      // email string for older call sites / tests.
      const body = typeof input === "string" ? { email: input } : input;
      // Validate at the boundary — the request body schema is the
      // SSOT for the email shape (lowercased, valid format).
      const parsed = MagicLinkRequestSchema.safeParse(body);
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? "Invalid email");
      }
      // Success path returns 202 with a constant body (email-
      // enumeration defense). Turnstile failure is a 403 —
      // portalFetch throws ApiError (not PortalAuthError) for that
      // code so the sign-in form can show a retry message.
      await portalFetch("/auth/magic-link/request", {
        method: "POST",
        body: JSON.stringify(parsed.data),
      });
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await portalFetch("/auth/magic-link/sign-out", { method: "POST" });
    },
    onSuccess: () => {
      // Drop the cached /me result; the next render of the portal
      // layout sees no session and redirects to sign-in.
      queryClient.setQueryData(PORTAL_ME_KEY, null);
      queryClient.invalidateQueries({ queryKey: PORTAL_ME_KEY });
    },
  });
}
